<!-- eslint-disable vue/prop-name-casing -->
<script setup>
// --------------------------------- Imports -------------------------------- //
import { cloneDeep, isEqual } from 'lodash-es';
import { getUserFullName, stringToNumber } from '~/common/utils/common.utils';

// ---------------------------------- Props --------------------------------- //
const props = defineProps({
  task: {
    type: Object,
    default: () => ({}),
  },
  assignees: {
    type: Object,
    default: () => ({}),
  },
  members: {
    type: Array,
    default: () => [],
  },
  teams: {
    type: Array,
    default: () => [],
  },
  active_schedule: {
    type: Object,
    default: () => ({}),
  },
  active_calendar_formatted: {
    type: Array,
    default: () => [],
  },
  on_close: {
    required: true,
  },
  on_save: {
    required: true,
  },
  is_schedule_editable: {
    type: Boolean,
    default: false,
  },
  translate: {
    type: Function,
    required: true,
  },
  view_only: {
    type: Boolean,
    default: false,
  },
  tippy_target: {
    required: true,
  },
});

// ----------------------- Variables - Pinia - consts ----------------------- //

// ------------------------ Variables - Local - refs ------------------------ //
const access_dropdown_trigger_refs = ref({});
const access_dropdown_menu_refs = ref({});
const accountable_editor_ref = ref(null);

// ---------------------- Variables - Local - reactives --------------------- //
const state = reactive({
  initial_state: {},
  assignees: [],
  members: [],
  teams: [],
  access_levels: [],
  is_saving: false,
  search: '',
  show_dropdown: false,
  show_access_dropdown: {},
  active_access_dropdown_index: null,
  dropdown_positions: {},
  selected_items: [],
});

// --------------------------- Computed properties -------------------------- //
const available_members_and_teams = computed(() => {
  const available_members = props.members.filter(member => !state.members.find(item => item.uid === member.uid)).map(item => ({
    ...item,
    type: 'member',
    display_name: getUserFullName(item),
  }));
  const available_teams = props.teams.filter(team => !state.teams.find(item => item.uid === team.uid)).map(item => ({
    ...item,
    type: 'team',
    display_name: item.name,
  }));
  return [...available_members, ...available_teams];
});

const filtered_members_and_teams = computed(() => {
  if (!state.search)
    return available_members_and_teams.value;

  const search_lower = state.search.toLowerCase();
  return available_members_and_teams.value.filter(r =>
    r.display_name.toLowerCase().includes(search_lower),
  );
});

const access_level_display = computed(() => {
  return (level) => {
    if (level === 'read')
      return props.translate('Viewer');
    if (level === 'manage')
      return props.translate('Manager');
    return level;
  };
});

const is_item_selected = computed(() => {
  return uid => state.selected_items.some(item => item.uid === uid);
});

// -------------------------------- Functions ------------------------------- //
async function onClose() {
  props?.on_close?.();
}

function toggleItemSelection(item) {
  const index = state.selected_items.findIndex(i => i.uid === item.uid);
  if (index > -1) {
    state.selected_items.splice(index, 1);
  }
  else {
    state.selected_items.push(item);
  }
}

function onAddSelectedItems() {
  if (state.selected_items.length === 0)
    return;

  state.selected_items.forEach((item) => {
    if (item.type === 'member') {
      const member_count = state.members.length;
      state.members.push(item);
      state.access_levels.splice(member_count, 0, 'read');
    }
    else {
      state.teams.push(item);
      state.access_levels.push('read');
    }
  });

  state.selected_items = [];
  state.search = '';
  state.show_dropdown = false;
}

function onSearchFocus() {
  state.show_dropdown = true;
}

function onSearchBlur() {
  setTimeout(() => {
    if (!state.show_dropdown)
      return;
    state.show_dropdown = false;
  }, 200);
}

function onRemoveMemberOrTeam(index) {
  const total_members = state.members.length;

  if (index < total_members) {
    state.members.splice(index, 1);
  }
  else {
    state.teams.splice(index - total_members, 1);
  }

  state.access_levels.splice(index, 1);
}

function onAccessLevelChange(index, level) {
  state.access_levels[index] = level;
  state.show_access_dropdown[index] = false;
  state.active_access_dropdown_index = null;
}

function calculateDropdownPosition(index) {
  nextTick(() => {
    const triggerEl = access_dropdown_trigger_refs.value[index];
    const editorEl = accountable_editor_ref.value;

    if (!triggerEl || !editorEl)
      return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();

    const top = triggerRect.bottom - editorRect.top + 4;
    const left = triggerRect.left - editorRect.left;
    const width = triggerRect.width;

    state.dropdown_positions[index] = { top, left, width };
  });
}

function handleEditorClick(event) {
  event.stopPropagation();

  if (state.active_access_dropdown_index !== null) {
    const index = state.active_access_dropdown_index;
    const triggerEl = access_dropdown_trigger_refs.value[index];
    const menuEl = access_dropdown_menu_refs.value[index];

    if (triggerEl && menuEl
      && !triggerEl.contains(event.target)
      && !menuEl.contains(event.target)) {
      state.show_access_dropdown[index] = false;
      state.active_access_dropdown_index = null;
    }
  }
}

function toggleAccessDropdown(index) {
  if (state.active_access_dropdown_index !== null && state.active_access_dropdown_index !== index) {
    state.show_access_dropdown[state.active_access_dropdown_index] = false;
  }

  const isOpening = !state.show_access_dropdown[index];
  state.show_access_dropdown[index] = isOpening;
  state.active_access_dropdown_index = isOpening ? index : null;

  if (isOpening) {
    calculateDropdownPosition(index);
  }
}

async function onSave() {
  state.is_saving = true;

  const assignees_list = [...state.members, ...state.teams].map((item, index) => {
    return {
      uid: item.uid,
      access_level: state.access_levels[index],
    };
  });

  const final_state = assignees_list.reduce((acc, item) => {
    acc[item.uid] = item.access_level;
    return acc;
  }, {});

  const has_changed = !isEqual(state.initial_state, final_state);
  if (has_changed) {
    setTimeout(async () => {
      await props.on_save(assignees_list);
      props.on_close();
      state.is_saving = false;
    }, props.is_schedule_editable ? 100 : 0);
  }
  else {
    props.on_close();
    state.is_saving = false;
  }
}

function truncate(str, length = 20, ellipses = '...') {
  if (!str)
    return '';

  const _str = String(str);

  if (_str.length <= length)
    return str;

  return _str.slice(0, length) + ellipses;
}

// -------------------------------- Watchers -------------------------------- //
watch(() => props.assignees, () => {
  state.assignees = Object.keys(props.assignees).map((item) => {
    return {
      uid: item,
      access_level: props.assignees[item],
    };
  });
  state.members = props.members.filter(member => state.assignees.find(item => item.uid === member.uid));
  state.teams = props.teams.filter(team => state.assignees.find(item => item.uid === team.uid));
  state.access_levels = state.assignees.map((item) => {
    return item.access_level || 'read';
  });

  state.initial_state = cloneDeep(props.assignees);
}, { immediate: true });

// ----------------------------- Lifecycle Hooks ---------------------------- //
</script>

<template>
  <div
    id="accountable-editor"
    ref="accountable_editor_ref"
    class="w-[500px] relative -mx-1"
    @click="handleEditorClick"
  >
    <div class="relative flex h-[52px] px-4 items-center gap-2">
      <div class="flex gap-2 flex-1">
        <div class="relative flex-1">
          <input
            v-model="state.search"
            type="text"
            class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            :placeholder="props.translate('Add accountable members')"
            @focus="onSearchFocus"
            @blur="onSearchBlur"
          >
          <div
            v-if="state.show_dropdown && filtered_members_and_teams.length > 0"
            class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-[200px] overflow-y-auto scrollbar"
            @mousedown.prevent
          >
            <div
              v-for="option in filtered_members_and_teams"
              :key="option.uid"
              class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
              :class="{ 'bg-primary-50': is_item_selected(option.uid) }"
              @click="toggleItemSelection(option)"
            >
              <HawkCheckbox
                :model-value="is_item_selected(option.uid)"
              />
              <IconIcOutlineGroups
                v-if="option.type === 'team'"
                class="w-5 h-5 text-primary-500"
              />
              <img
                v-else-if="option.type === 'member' && option.display_picture"
                alt="dp"
                :src="option.display_picture"
                class="inline w-5 h-5 rounded-full"
              >
              <span
                v-else-if="option.type === 'member'"
                class="inline-flex items-center justify-center w-5 h-5 text-xs leading-none text-white rounded-full"
                :style="`background-color: ${stringToNumber(getUserFullName(option))}`"
              >
                {{ option.first_name?.charAt(0) }}
              </span>
              <span class="text-sm text-gray-900">{{ option.display_name }}</span>
            </div>
          </div>
          <div
            v-else-if="
              state.show_dropdown
                && (
                  (state.search && filtered_members_and_teams.length === 0)
                  || available_members_and_teams.length === 0
                )"
            class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg"
          >
            <div class="px-3 py-2 text-sm text-gray-500 italic">
              {{ props.translate('No results found') }}
            </div>
          </div>
        </div>
        <HawkButton
          :disabled="state.selected_items.length === 0"
          @click="onAddSelectedItems"
        >
          {{ props.translate('Add') }}
        </HawkButton>
      </div>
    </div>
    <hr class="-mx-1 mt-1">
    <div class="px-4 pt-4 pb-1">
      <template v-if="state.members.length + state.teams.length">
        <div class="-m-4 py-4 pl-4 max-h-[300px] overflow-y-scroll scrollbar">
          <!-- Header -->
          <div class="grid gap-x-3 mb-2 grid-cols-11">
            <div class="text-sm font-normal text-gray-600 col-span-6">
              {{ props.translate('Accountable') }}
            </div>
            <div class="text-sm font-normal text-gray-600 col-span-4">
              {{ props.translate('Access level') }}
            </div>
            <div class="text-sm font-normal text-gray-600 col-span-1" />
          </div>
          <!-- Members -->
          <template
            v-for="(member, index) in state.members"
            :key="member.uid"
          >
            <div class="grid gap-x-3 py-2 pl-2 -ml-2 rounded-lg group hover:bg-gray-50 grid-cols-11">
              <div class="flex items-center text-sm font-normal text-gray-900 col-span-6">
                <img
                  v-if="member.display_picture"
                  alt="dp"
                  :src="member.display_picture"
                  class="inline w-6 h-6 rounded-full"
                >
                <span
                  v-else
                  class="inline-flex items-center justify-center w-6 h-6 p-1 leading-none text-white rounded-full"
                  :style="`background-color: ${stringToNumber(getUserFullName(member))}`"
                >
                  {{ member.first_name?.charAt(0) }}
                </span>
                <span class="inline-flex items-center ml-3 text-sm font-normal text-gray-900">
                  {{ truncate(member.first_name ? `${member.first_name} ${member.last_name || ''}` : member.email, 30) }}
                </span>
              </div>
              <div class="relative flex items-center col-span-4">
                <div
                  :ref="el => { if (el) access_dropdown_trigger_refs[index] = el }"
                  class="flex items-center py-1.5 cursor-pointer"
                  @click="toggleAccessDropdown(index)"
                >
                  <span class="text-sm text-gray-900">{{ access_level_display(state.access_levels[index]) }}</span>
                  <IconHawkChevronDown v-if="state.active_access_dropdown_index !== index" class="w-4 h-4 text-gray-600" />
                  <IconHawkChevronUp v-else class="w-4 h-4 text-gray-600" />
                </div>
              </div>
              <div class="flex items-center col-span-1">
                <IconHawkTrashOne
                  class="w-5 h-5 text-gray-400 cursor-pointer hover:text-error-500"
                  @click="onRemoveMemberOrTeam(index)"
                />
              </div>
            </div>
          </template>
          <!-- Teams -->
          <template
            v-for="(team, index) in state.teams"
            :key="team.uid"
          >
            <div class="grid gap-x-3 py-2 pl-2 -ml-2 rounded-lg group hover:bg-gray-50 grid-cols-11">
              <div class="flex items-center text-sm font-normal text-gray-900 col-span-6">
                <IconIcOutlineGroups
                  class="w-6 h-6 text-primary-500"
                />
                <span class="ml-3 break-all">
                  {{ truncate(team.name, 30) }}
                </span>
              </div>
              <div class="relative flex items-center col-span-4">
                <div
                  :ref="el => { if (el) access_dropdown_trigger_refs[state.members.length + index] = el }"
                  class="flex items-center py-1.5 cursor-pointer"
                  @click="toggleAccessDropdown(state.members.length + index)"
                >
                  <span class="text-sm text-gray-900">{{ access_level_display(state.access_levels[state.members.length + index]) }}</span>
                  <IconHawkChevronDown v-if="state.active_access_dropdown_index !== state.members.length + index" class="w-4 h-4 text-gray-600" />
                  <IconHawkChevronUp v-else class="w-4 h-4 text-gray-600" />
                </div>
              </div>
              <div class="flex items-center col-span-1">
                <IconHawkTrashOne
                  class="w-5 h-5 text-gray-400 cursor-pointer hover:text-error-500"
                  @click="onRemoveMemberOrTeam(state.members.length + index)"
                />
              </div>
            </div>
          </template>
        </div>
      </template>
      <div v-else class="text-sm text-gray-400 italic">
        {{ props.translate('No accountable members added for this activity.') }}
      </div>
      <hr class="mt-4 mb-2 -mx-4">
      <div class="flex items-center justify-end">
        <div class="text-sm font-semibold text-gray-600 mr-2 cursor-pointer" @click="onClose">
          {{ props.translate('Close') }}
        </div>
        <div v-if="!state.is_saving" class="text-sm font-semibold text-primary-700 cursor-pointer" @click="onSave">
          {{ props.translate('Save') }}
        </div>
        <div v-else class="w-[33px]">
          <HawkLoader container_class="" :height="4" :width="4" />
        </div>
      </div>
    </div>

    <!-- Access Level Dropdowns - Positioned absolutely relative to #accountable-editor -->
    <template v-for="(_, dropdownIndex) in [...state.members, ...state.teams]" :key="`dropdown-${dropdownIndex}`">
      <div
        v-if="state.show_access_dropdown[dropdownIndex] && state.dropdown_positions[dropdownIndex]"
        :ref="el => { if (el) access_dropdown_menu_refs[dropdownIndex] = el }"
        class="absolute z-[100] bg-white border border-gray-300 rounded-lg shadow-lg p-1"
        :style="{
          top: `${state.dropdown_positions[dropdownIndex].top}px`,
          left: `${state.dropdown_positions[dropdownIndex].left}px`,
          width: '240px',
        }"
      >
        <div
          class="px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-lg"
          @click="onAccessLevelChange(dropdownIndex, 'read')"
        >
          <div class="text-sm font-medium text-gray-700">
            {{ props.translate('Viewer') }}
          </div>
          <div class="text-xs font-normal text-gray-600">
            {{ props.translate('Can only view WBS/activities') }}
          </div>
        </div>
        <div
          class="px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-lg"
          @click="onAccessLevelChange(dropdownIndex, 'manage')"
        >
          <div class="text-sm font-medium text-gray-700">
            {{ props.translate('Manager') }}
          </div>
          <div class="text-xs font-normal text-gray-600">
            {{ props.translate('Can view and modify WBS/activities') }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
